import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Search, ChevronDown, HelpCircle, ArrowRight, Sparkles, MessageCircle, Info } from "lucide-react";

interface FAQItem {
  id: string;
  question: string;
  answer: string;
}

export default function FAQ() {
  const [activeId, setActiveId] = useState<string | null>("faq-1");
  const [searchQuery, setSearchQuery] = useState("");

  const faqItems: FAQItem[] = [
    {
      id: "faq-1",
      question: "How accurate is plant identification?",
      answer: "Our photo identification model is highly accurate when provided with a clear, well-focused photo. Results are based on visual patterns of flowers, leaves, and buds, and should be treated as educational suggestions. We recommend cross-referencing with local educational institutions or professional physical field guides for critical validation.",
    },
    {
      id: "faq-2",
      question: "Can I identify plants from leaves?",
      answer: "Yes, our system can identify a large percentage of tree, flower, and houseplant species by analyzing visual features of a single leaf, such as veins, leaf shape, margins, and surface texture. For the most accurate results, including flowers or berries in the image is recommended.",
    },
    {
      id: "faq-3",
      question: "Can I identify flowers?",
      answer: "Absolutely. Flowers have highly distinctive characteristics (petal shape, arrangement, colors, stamens) that make them exceptionally easy to recognize and cross-reference within our educational database.",
    },
    {
      id: "faq-4",
      question: "Is the service free?",
      answer: "Yes, our educational plant identification resources and database remain 100% free to access. We believe that botanical information and nature appreciation should be universally accessible to gardeners, students, and educators.",
    },
    {
      id: "faq-5",
      question: "What image quality works best?",
      answer: "A crisp, high-resolution photo taken in good, natural light works best. Try to isolate the single target plant or leaf, keep it in sharp focus, and avoid blurry backgrounds, high shadows, or containing multiple different species in a single frame.",
    },
    {
      id: "faq-6",
      question: "How do I care for identified plants?",
      answer: "Once you identify your plant, click on its care card to learn specific guidelines regarding light requirements, watering schedules, suitable soil type, and temperature ranges to help keep your plant healthy.",
    },
    {
      id: "faq-7",
      question: "Can I identify trees?",
      answer: "Yes. By uploading close-up photos of leaves, bark, fruits, flowers, or buds, you can successfully identify a vast variety of coniferous, deciduous, and ornamental tree species from around the world.",
    },
    {
      id: "faq-8",
      question: "Can I identify weeds?",
      answer: "Yes, our botanical database features extensive cataloging of common garden weeds, invasive herbs, and wild flora. Identifying weeds helps gardeners decide whether to tolerate, prune, or relocate self-seeded specimens.",
    },
    {
      id: "faq-9",
      question: "Can the AI detect plant diseases?",
      answer: "Yes, our advanced image model can identify common plant pathogens, fungal infections, rusts, mildews, and nutrient deficiencies. By examining leaf discoloration and lesion patterns, it suggests corrective care steps.",
    },
    {
      id: "faq-10",
      question: "Does the platform support botanical scientific names?",
      answer: "Yes, every plant profile in our database is cataloged with its accepted binomial nomenclature (Scientific Name), Family, Genus, and Species, alongside popular regional common names for complete scientific accuracy.",
    },
    {
      id: "faq-11",
      question: "Can I identify seedlings or young sprouts?",
      answer: "Seedlings can be identified, but accuracy is generally lower during early cotyledon development. Once the plant develops its first set of 'true leaves' showing mature margins and venation, our accuracy increases significantly.",
    },
    {
      id: "faq-12",
      question: "Are there warnings for toxic or poisonous plants?",
      answer: "Yes, our plant details pages highlight safety classifications. If a scanned plant is toxic to humans, dogs, or cats (such as certain nightshades or lilies), we display prominent alerts to prevent accidental contact or ingestion.",
    },
    {
      id: "faq-13",
      question: "What taxonomic reference database does this system use?",
      answer: "Our taxonomy aligns with major international botanical authorities, including the Royal Botanic Gardens, Kew (Plants of the World Online), the Integrated Taxonomic Information System (ITIS), and the USDA Plant Database.",
    },
    {
      id: "faq-14",
      question: "Is there a mobile app available for download?",
      answer: "Yes! While our website is fully responsive, you can download our direct Android APK via the 'Get App' button in the header. This provides offline reference capabilities and a local scanning history log.",
    },
    {
      id: "faq-15",
      question: "How is my uploaded scanning data handled?",
      answer: "We prioritize your privacy. Uploaded photos are processed securely to generate visual matches. If you create a free account, you can opt to save your scan history to your dashboard, which you can delete at any time.",
    },
    {
      id: "faq-16",
      question: "What is soil pH and how does the analyzer help?",
      answer: "Soil pH measures acidity or alkalinity, which directly affects nutrient availability. Our online Soil pH Analyzer tool recommends target pH ranges and amendments (like sulfur or lime) tailored to your specific plant species."
    }
  ];

  // Live filter based on query
  const filteredFaqs = useMemo(() => {
    if (!searchQuery.trim()) return faqItems;
    const q = searchQuery.toLowerCase();
    return faqItems.filter(
      (item) =>
        item.question.toLowerCase().includes(q) ||
        item.answer.toLowerCase().includes(q)
    );
  }, [searchQuery]);

  const toggleAccordion = (id: string) => {
    setActiveId((prev) => (prev === id ? null : id));
  };

  const handleContactClick = () => {
    const contactSection = document.getElementById("contact");
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: "smooth" });
    } else {
      // Fallback
      alert("Please scroll to the footer or contact our mail support directly at contact@plantidentifer.com!");
    }
  };

  return (
    <section 
      id="faq" 
      className="pt-16 pb-6 bg-white relative overflow-hidden"
      aria-labelledby="faq-title"
    >
      {/* Premium organic backdrop radial lights */}
      <div className="absolute top-1/3 left-1/4 -translate-y-1/2 w-96 h-96 bg-green-500/[0.015] rounded-full blur-[120px] pointer-events-none -z-10 animate-pulse" />
      <div className="absolute bottom-1/4 right-[10%] w-[500px] h-[500px] bg-emerald-400/[0.02] rounded-full blur-[140px] pointer-events-none -z-10" />

      {/* Floating leaves SVG decorations for rich nature vibes */}
      <div className="absolute right-[5%] top-24 w-16 h-16 opacity-[0.02] pointer-events-none hover:rotate-45 transition-transform duration-500">
        <svg viewBox="0 0 24 24" fill="currentColor" className="text-green-700">
          <path d="M17 8C8 10 5 16 5 16s4-4 12-4h2v2c0 2-4 4-4 4s11 0 11-8v-2h-4z" />
        </svg>
      </div>

      <div className="max-w-4xl mx-auto px-6 relative z-10">
        
        {/* SECTION HEADER BLOCK */}
        <div className="text-center max-w-3xl mx-auto mb-20 space-y-4">
          
          {/* Badge Glass Pill */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-1.5 px-3.5 py-1 bg-green-50/90 border border-green-150/40 rounded-full text-green-700 text-xs font-extrabold uppercase tracking-widest shadow-2xs"
          >
            <span>❓</span> Frequently Asked Questions
          </motion.div>

          {/* Heading with proper semantic structure */}
          <motion.h2
            id="faq-title"
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-4xl sm:text-5xl font-[900] text-gray-950 tracking-tight leading-[1.12]"
          >
            Everything You Need <br />
            To Know
          </motion.h2>

          {/* Subheading */}
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.16 }}
            className="text-lg text-gray-500 font-medium max-w-[700px] mx-auto leading-relaxed"
          >
            Find answers to the most common questions about PlantID AI and plant identification.
          </motion.p>

          {/* Premium Search Accordion Filtration Input */}
          <motion.div 
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.22 }}
            className="max-w-md mx-auto pt-6"
          >
            <div className="relative group">
              <div className="absolute inset-0 bg-green-200/20 rounded-2xl blur-lg opacity-0 group-focus-within:opacity-100 transition-opacity" />
              <div className="relative flex items-center bg-gray-55/30 hover:bg-gray-55/65 group-focus-within:bg-white border border-gray-150/50 group-focus-within:border-green-300 rounded-2xl px-4 py-3 transition-all duration-200 shadow-3xs">
                <Search className="w-5 h-5 text-gray-400 group-focus-within:text-green-600 shrink-0 mr-3" />
                <input 
                  type="text" 
                  placeholder="Search Questions..." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-transparent border-0 outline-hidden text-sm font-extrabold text-gray-900 placeholder:text-gray-450 focus:ring-0 leading-none"
                />
                {searchQuery && (
                  <button 
                    onClick={() => setSearchQuery("")}
                    className="text-[10px] uppercase font-black text-gray-400 hover:text-gray-700 bg-gray-100 px-2 py-0.5 rounded cursor-pointer"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
            {searchQuery && (
              <span className="text-[10px] text-gray-400 font-bold block mt-2 text-left px-1">
                Filtered and found <strong className="text-green-700">{filteredFaqs.length}</strong> matching result{filteredFaqs.length === 1 ? "" : "s"}.
              </span>
            )}
          </motion.div>

        </div>

        {/* ACCORDION FAQ CONTAINER (SEO Semantic FAQPage Markup ready) */}
        <div 
          className="space-y-4"
          itemScope 
          itemType="https://schema.org/FAQPage"
        >
          <AnimatePresence initial={false}>
            {filteredFaqs.length > 0 ? (
              filteredFaqs.map((faq, index) => {
                const isOpen = activeId === faq.id;
                return (
                  <motion.div
                    key={faq.id}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ duration: 0.4, delay: Math.min(index * 0.05, 0.3) }}
                    itemProp="mainEntity"
                    itemScope
                    itemType="https://schema.org/Question"
                    className={`bg-white rounded-2xl border transition-all duration-300 overflow-hidden relative group shadow-[0_4px_20px_rgba(0,0,0,0.012)] ${
                      isOpen 
                        ? "border-green-200 shadow-[0_12px_28px_rgba(34,197,94,0.035)]" 
                        : "border-gray-150 hover:border-gray-250 hover:shadow-[0_8px_24px_rgba(0,0,0,0.02)]"
                    }`}
                  >
                    {/* Left green marker tab indicator */}
                    <div className={`absolute left-0 top-0 bottom-0 w-1 transition-all duration-300 ${
                      isOpen ? "bg-green-600 scale-y-100" : "bg-transparent scale-y-0"
                    }`} />

                    {/* Trigger Button with heading tag */}
                    <h3 className="m-0">
                      <button
                        onClick={() => toggleAccordion(faq.id)}
                        aria-expanded={isOpen}
                        aria-controls={`answer-${faq.id}`}
                        className="w-full text-left py-5 px-6 sm:px-8 flex items-center justify-between gap-4 cursor-pointer"
                      >
                        <span 
                          itemProp="name"
                          className={`text-sm sm:text-base font-black tracking-tight leading-snug transition-colors duration-200 ${
                            isOpen ? "text-green-800" : "text-gray-950 group-hover:text-green-700"
                          }`}
                        >
                          {faq.question}
                        </span>
                        
                        <div className={`w-8 h-8 rounded-full border flex items-center justify-center shrink-0 transition-all duration-200 ${
                          isOpen 
                            ? "bg-green-50 border-green-200 text-green-700 rotate-180" 
                            : "bg-gray-50 border-gray-100 text-gray-400 group-hover:border-gray-300 group-hover:text-gray-700"
                        }`}>
                          <ChevronDown className="w-4 h-4 shrink-0 transition-transform" />
                        </div>
                      </button>
                    </h3>

                    {/* Expandable answer panel logic */}
                    <div 
                      id={`answer-${faq.id}`}
                      role="region" 
                      aria-labelledby={`faq-trigger-${faq.id}`}
                      itemProp="acceptedAnswer"
                      itemScope
                      itemType="https://schema.org/Answer"
                      className={`transition-all duration-300 ease-in-out ${
                        isOpen ? "max-h-[300px]" : "max-h-0"
                      }`}
                    >
                      <div className="pb-6 px-6 sm:px-8 pt-1 text-xs sm:text-sm text-gray-500 font-semibold leading-relaxed">
                        <p itemProp="text">
                          {faq.answer}
                        </p>
                      </div>
                    </div>

                  </motion.div>
                );
              })
            ) : (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="p-12 text-center rounded-2xl border border-dashed border-gray-200 bg-gray-50"
              >
                <Info className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                <span className="text-sm font-black text-gray-800 block">No answers match your query</span>
                <p className="text-xs text-gray-400 mt-1 max-w-sm mx-auto leading-relaxed">
                  Try asking with shorter keywords like "free", "mobile", "accuracy", or "account" to locate matching database details.
                </p>
                <button 
                  onClick={() => setSearchQuery("")}
                  className="mt-4 px-4 py-1.5 bg-green-50 hover:bg-green-100 border border-green-100 text-green-700 rounded-lg text-xs font-black cursor-pointer inline-flex items-center gap-1.5"
                >
                  Reset Filtration Search
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* STILL HAVE QUESTIONS CALL TO ACTION CARD */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-12 bg-gradient-to-br from-gray-950 to-slate-900 border border-slate-850 rounded-3xl p-8 sm:p-10 text-white text-center relative overflow-hidden shadow-xl"
        >
          {/* Subtle decoration vector glows */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-green-500/10 rounded-full blur-xl pointer-events-none" />
          <div className="absolute bottom-[-32px] left-[-32px] w-48 h-48 bg-emerald-500/10 rounded-full blur-2xl pointer-events-none" />

          <div className="space-y-6 relative z-10 max-w-lg mx-auto">
            <span className="text-3xl">💬</span>
            <div className="space-y-2">
              <h3 className="text-2xl sm:text-3xl font-[900] tracking-tight leading-none text-white">
                Still Have Questions?
              </h3>
              <p className="text-xs sm:text-sm text-gray-400 leading-relaxed font-bold">
                Our collaborative community botanists and AI researchers are always working to improve PlantID AI and help you learn more about nature.
              </p>
            </div>

            <div className="pt-2">
              <button
                onClick={handleContactClick}
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-gradient-to-r from-green-500 to-emerald-400 hover:from-green-600 hover:to-emerald-500 text-white rounded-2xl shadow-md text-xs font-black transition-all cursor-pointer"
              >
                Contact Our Team
                <ArrowRight className="w-4 h-4 shrink-0 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
}
